import { formatAdministrativeExpensesRate } from '@/features/mold/utils/ks-standard-list/private-csv';
import type {
  FundAdditionalIncomeAndExpenditureModel,
  FundAdditionalIncomeAndExpenditureType,
  PeriodScopeType,
} from '@/features/project/projects/_models/fund-additiona-income-and-expenditure';
import { isBefore2025 } from '@/features/project/review-sheet/utils/project';
import { t } from '@/i18n';
import {
  BigCalculator,
  FALLBACK_TEXT,
  convertToNegative,
  handleZeroValueWithNegativeCount,
} from '@/utils';
import type { z } from 'zod';

type ReturnValue = {
  value: string | number | null | undefined;
  isAggregateValueZero: boolean;
};

export type ConvertPriorToList = typeof convertPriorToList;
export const convertPriorToList = (
  value: FundAdditionalIncomeAndExpenditureType,
): FundAdditionalIncomeAndExpenditureType[] => {
  const list: FundAdditionalIncomeAndExpenditureType[] = [];

  const addPriorToList = (
    incomeAndExpenditureData:
      | FundAdditionalIncomeAndExpenditureType
      | null
      | undefined,
  ) => {
    if (incomeAndExpenditureData) {
      list.push(incomeAndExpenditureData);
      addPriorToList(incomeAndExpenditureData.prior);
    }
  };

  addPriorToList(value.prior);

  return list;
};

const getSameFieldByYear = (
  yearKeys: number[],
  fundItemMap: Record<string, FundAdditionalIncomeAndExpenditureType>,
  fieldName: string,
): Record<string, number> => {
  const result = yearKeys.reduce<Record<string, number>>((acc, year) => {
    acc[year] = fundItemMap[year]?.[fieldName] ?? '';
    return acc;
  }, {});

  return result;
};

export type CalcAdministrativeExpensesRate =
  typeof calcAdministrativeExpensesRate;
export const calcAdministrativeExpensesRate = (
  administrativeExpense: number,
  sum: string | number | bigint | null | undefined,
) => {
  const formattedSum = typeof sum === 'number' ? sum : 0;
  const value =
    formattedSum === 0
      ? 0
      : BigCalculator.divide(administrativeExpense, formattedSum);

  return formatAdministrativeExpensesRate(value);
};

const getAdministrativeExpensesRateByYear = (
  yearKeys: number[],
  fundItemMap: Record<string, FundAdditionalIncomeAndExpenditureType>,
  isDebugMode: boolean,
): Record<string, string> => {
  const result = yearKeys.reduce<Record<string, string>>((acc, year) => {
    const currentFundItem = fundItemMap[year];
    if (currentFundItem) {
      const administrativeExpense = currentFundItem.administrativeExpense ?? 0;
      const summary = getExpenditureSummaryByType(
        currentFundItem,
        'previous-year-performance',
        isDebugMode,
      );

      acc[year] = calcAdministrativeExpensesRate(
        administrativeExpense,
        summary.value,
      );
    }
    return acc;
  }, {});
  return result;
};

export const getTotalValue = (
  targets: (number | undefined | null)[],
  isDebugMode: boolean,
): ReturnValue => {
  const numberTargets = targets.flatMap((v) =>
    typeof v === 'number' ? [v] : [],
  );
  if (numberTargets.every((v) => v === undefined || v === null)) {
    return { value: FALLBACK_TEXT, isAggregateValueZero: false };
  }

  const hasNegativeValue = numberTargets.some((v) => v < 0);
  const value = numberTargets.reduce((acc, v) => BigCalculator.add(acc, v), 0);

  const { fallbackValue, isAggregateValueZero } =
    handleZeroValueWithNegativeCount(value, {
      negativeCount: hasNegativeValue ? 1 : 0,
      isDebugMode,
    });
  return { value: fallbackValue, isAggregateValueZero };
};

export type GetIncomeSummaryByType = typeof getIncomeSummaryByType;
export const getIncomeSummaryByType = (
  fundAdditionalIncomeAndExpenditure: FundAdditionalIncomeAndExpenditureType,
  type: PeriodScopeType,
  isDebugMode: boolean,
): ReturnValue => {
  if (type === 'previous-year-performance') {
    const targets = [
      fundAdditionalIncomeAndExpenditure.grantedAmountByTheCountry,
      fundAdditionalIncomeAndExpenditure.investmentIncome,
      fundAdditionalIncomeAndExpenditure.businessIncome,
      fundAdditionalIncomeAndExpenditure.otherIncomeAmount,
    ];
    return getTotalValue(targets, isDebugMode);
  }
  if (type === 'estimated-this-year') {
    const targets = [
      fundAdditionalIncomeAndExpenditure.estimatedGrantedAmountByTheCountry,
      fundAdditionalIncomeAndExpenditure.estimatedInvestmentIncome,
      fundAdditionalIncomeAndExpenditure.estimatedBusinessIncome,
      fundAdditionalIncomeAndExpenditure.estimatedOtherIncomeAmount,
    ];
    return getTotalValue(targets, isDebugMode);
  }
  const targets = [
    fundAdditionalIncomeAndExpenditure.estimatedGrantedAmountByTheCountryNextYear,
    fundAdditionalIncomeAndExpenditure.estimatedInvestmentIncomeNextYear,
    fundAdditionalIncomeAndExpenditure.estimatedBusinessIncomeNextYear,
    fundAdditionalIncomeAndExpenditure.estimatedOtherIncomeAmountNextYear,
  ];
  return getTotalValue(targets, isDebugMode);
};

export type GetExpenditureSummaryByType = typeof getExpenditureSummaryByType;
export const getExpenditureSummaryByType = (
  fundAdditionalIncomeAndExpenditure: FundAdditionalIncomeAndExpenditureType,
  type: PeriodScopeType,
  isDebugMode: boolean,
): ReturnValue => {
  if (type === 'previous-year-performance') {
    const targets = [
      fundAdditionalIncomeAndExpenditure.businessExpense,
      fundAdditionalIncomeAndExpenditure.administrativeExpense,
    ];
    return getTotalValue(targets, isDebugMode);
  }
  if (type === 'estimated-this-year') {
    const targets = [
      fundAdditionalIncomeAndExpenditure.estimatedBusinessExpense,
      fundAdditionalIncomeAndExpenditure.estimatedAdministrativeExpense,
    ];
    return getTotalValue(targets, isDebugMode);
  }

  const targets = [
    fundAdditionalIncomeAndExpenditure.estimatedBusinessExpenseNextYear,
    fundAdditionalIncomeAndExpenditure.estimatedAdministrativeExpenseNextYear,
  ];
  return getTotalValue(targets, isDebugMode);
};

export type GetIncomeAndExpenditureSummaryByType =
  typeof getIncomeAndExpenditureSummaryByType;

export const getIncomeAndExpenditureSummaryByType = (
  fundAdditionalIncomeAndExpenditure: FundAdditionalIncomeAndExpenditureType,
  type: PeriodScopeType,
  getIncomeSummaryByType: GetIncomeSummaryByType,
  getExpenditureSummaryByType: GetExpenditureSummaryByType,
  isDebugMode: boolean,
): ReturnValue => {
  const incomeSummary = getIncomeSummaryByType(
    fundAdditionalIncomeAndExpenditure,
    type,
    isDebugMode,
  );

  const expenditureSummary = getExpenditureSummaryByType(
    fundAdditionalIncomeAndExpenditure,
    type,
    isDebugMode,
  );

  const incomeAndExpenditureSummary =
    (typeof incomeSummary.value === 'number' ? incomeSummary.value : 0) -
    (typeof expenditureSummary.value === 'number'
      ? expenditureSummary.value
      : 0);

  if (type === 'previous-year-performance') {
    const targets = [
      fundAdditionalIncomeAndExpenditure.beginningOfTheFundBalance,
      incomeAndExpenditureSummary,
      convertToNegative(
        fundAdditionalIncomeAndExpenditure.amountReturnedToTheNationalTreasury,
      ),
      convertToNegative(
        fundAdditionalIncomeAndExpenditure.amountReturnedToTheOrganization,
      ),
    ];

    return getTotalValue(targets, isDebugMode);
  }
  if (type === 'estimated-this-year') {
    const targets = [
      fundAdditionalIncomeAndExpenditure.beginningOfTheFundBalance,
      incomeAndExpenditureSummary,
      convertToNegative(
        fundAdditionalIncomeAndExpenditure.estimatedAmountReturnedToTheNationalTreasury,
      ),
      convertToNegative(
        fundAdditionalIncomeAndExpenditure.estimatedAmountReturnedToTheOrganization,
      ),
    ];

    return getTotalValue(targets, isDebugMode);
  }

  const targets = [
    fundAdditionalIncomeAndExpenditure.beginningOfTheEstimatedFundBalanceNextYear,
    incomeAndExpenditureSummary,
    convertToNegative(
      fundAdditionalIncomeAndExpenditure.estimatedAmountReturnedToTheNationalTreasuryNextYear,
    ),
    convertToNegative(
      fundAdditionalIncomeAndExpenditure.estimatedAmountReturnedToTheOrganizationNextYear,
    ),
  ];
  return getTotalValue(targets, isDebugMode);
};

const getYearToValueRecordOfFunc = (
  yearKeys: number[],
  fundItemMap: Record<string, FundAdditionalIncomeAndExpenditureType>,
  type: PeriodScopeType,
  getValueByType: GetIncomeSummaryByType,
  isDebugMode: boolean,
): Record<string, string | number | bigint | null | undefined> => {
  const result = yearKeys.reduce<
    Record<string, string | number | bigint | null | undefined>
  >((acc, year) => {
    const currentFundItem = fundItemMap[year];
    if (currentFundItem) {
      const { value } = getValueByType(currentFundItem, type, isDebugMode);
      acc[year] = value;
    }
    return acc;
  }, {});
  return result;
};

const getIncomeAndExpenditureSummariesByYear = (
  yearKeys: number[],
  fundItemMap: Record<string, FundAdditionalIncomeAndExpenditureType>,
  type: PeriodScopeType,
  getIncomeAndExpenditureSummaryByType: GetIncomeAndExpenditureSummaryByType,
  getIncomeSummaryByType: GetIncomeSummaryByType,
  getExpenditureSummaryByType: GetExpenditureSummaryByType,
  isDebugMode: boolean,
): Record<string, string | number | null | undefined> => {
  const result = yearKeys.reduce<
    Record<string, string | number | null | undefined>
  >((acc, year) => {
    const fundItem = fundItemMap[year];
    if (!fundItem) {
      return acc;
    }
    acc[year] = getIncomeAndExpenditureSummaryByType(
      fundItem,
      type,
      getIncomeSummaryByType,
      getExpenditureSummaryByType,
      isDebugMode,
    ).value;
    return acc;
  }, {});
  return result;
};

export type GetClericalExpenditureSummaryByType =
  typeof getClericalExpenditureSummaryByType;
export const getClericalExpenditureSummaryByType = (
  fundAdditionalIncomeAndExpenditure: FundAdditionalIncomeAndExpenditureType,
  type: PeriodScopeType,
  isDebugMode: boolean,
): ReturnValue => {
  if (type === 'previous-year-performance') {
    const targets = [
      fundAdditionalIncomeAndExpenditure.choreExpensesNotFromThisFund,
      fundAdditionalIncomeAndExpenditure.personnelExpensesNotFromThisFund,
    ];
    return getTotalValue(targets, isDebugMode);
  }
  if (type === 'estimated-this-year') {
    const targets = [
      fundAdditionalIncomeAndExpenditure.estimatedChoreExpensesNotFromThisFund,
      fundAdditionalIncomeAndExpenditure.estimatedPersonnelExpensesNotFromThisFund,
    ];
    return getTotalValue(targets, isDebugMode);
  }

  const targets = [
    fundAdditionalIncomeAndExpenditure.estimatedChoreExpensesNotFromThisFundNextYear,
    fundAdditionalIncomeAndExpenditure.estimatedPersonnelExpensesNotFromThisFundNextYear,
  ];
  return getTotalValue(targets, isDebugMode);
};

export type FundIncomeAndExpenditureDataKey =
  | 'fund-balance-at-the-end-of-previous-fiscal-year'
  | 'income'
  | 'investment-income'
  | 'national-equivalent-amount-of-investment-income'
  | 'business-income'
  | 'national-equivalent-amount-of-business-income'
  | 'others-income'
  | 'total-amount'
  | 'expense'
  | 'administrative-expenses'
  | 'administrative-expenses-rate'
  | 'chore-expenses-of-fund-establishing-of-that'
  | 'personnel-expenses-of-fund-establishing-of-that'
  | 'total-epense-amount'
  | 'returned-to-the-national-treasury'
  | 'returned-to-the-organization'
  | 'fund-balance-at-the-end-of-fiscal-year'
  | 'national-equivalent-amount-of-fund-balance'
  | 'clerical-personnel-expenses'
  | 'personnel-expense'
  | 'total-amount-clerical-expenses';

type YearValue = {
  value: number | string | null | undefined;
  isAggregateValueZero: boolean;
};

// TODO: 過去年度のobjectについては型指定できていない。対応するためには生成ロジックの見直しが必要なので、一旦このようにしている。
export type FundIncomeAndExpenditureDataSourceType = {
  currentYear: YearValue;
  nextYear: YearValue;
  key: FundIncomeAndExpenditureDataKey;
  mainHeaderName: string;
  headerName: string | null;
};

export const getFundIncomeAndExpenditureDataSource = ({
  fiscalYear,
  isDebugMode,
  pastFundAdditionalIncomeAndExpenditureListSortedByFiscalYear,
  data,
}: {
  fiscalYear: number;
  isDebugMode: boolean;
  data: z.infer<typeof FundAdditionalIncomeAndExpenditureModel>;
  pastFundAdditionalIncomeAndExpenditureListSortedByFiscalYear: (FundAdditionalIncomeAndExpenditureType & {
    fiscalYear: number;
  })[];
}): FundIncomeAndExpenditureDataSourceType[] => {
  const pastYearKeys =
    pastFundAdditionalIncomeAndExpenditureListSortedByFiscalYear.map(
      (value) => value.fiscalYear,
    );
  const getYearToFundItemMap = () => {
    const yearToFundItemMap =
      pastFundAdditionalIncomeAndExpenditureListSortedByFiscalYear.reduce<
        Record<string, FundAdditionalIncomeAndExpenditureType>
      >((acc, current) => {
        acc[current.fiscalYear] = current;
        return acc;
      }, {});

    return yearToFundItemMap;
  };

  const yearToFundItemMap = getYearToFundItemMap();
  return [
    {
      key: 'fund-balance-at-the-end-of-previous-fiscal-year',
      mainHeaderName: t('fund-balance-at-the-end-of-previous-fiscal-year'),
      headerName: null,
      ...getSameFieldByYear(
        pastYearKeys,
        yearToFundItemMap,
        'beginningOfTheFundBalance',
      ),
      currentYear: {
        value: data.beginningOfTheFundBalance,
        isAggregateValueZero: false,
      },
      nextYear: {
        value: data.beginningOfTheEstimatedFundBalanceNextYear,
        isAggregateValueZero: false,
      },
    },
    {
      key: 'income',
      mainHeaderName: t('income'),
      headerName: t('granted-amount-by-the-country'),
      ...getSameFieldByYear(
        pastYearKeys,
        yearToFundItemMap,
        'grantedAmountByTheCountry',
      ),
      currentYear: {
        value: data.estimatedGrantedAmountByTheCountry,
        isAggregateValueZero: false,
      },
      nextYear: {
        value: data.estimatedGrantedAmountByTheCountryNextYear,
        isAggregateValueZero: false,
      },
    },
    {
      key: 'investment-income',
      mainHeaderName: 'hidden',
      headerName: t('investment-income'),
      ...getSameFieldByYear(
        pastYearKeys,
        yearToFundItemMap,
        'investmentIncome',
      ),
      currentYear: {
        value: data.estimatedInvestmentIncome,
        isAggregateValueZero: false,
      },
      nextYear: {
        value: data.estimatedInvestmentIncomeNextYear,
        isAggregateValueZero: false,
      },
    },
    {
      key: 'national-equivalent-amount-of-investment-income',
      mainHeaderName: 'hidden',
      headerName: t('national-equivalent-amount-of-that'),
      ...getSameFieldByYear(
        pastYearKeys,
        yearToFundItemMap,
        'nationalEquivalentOfInvestmentIncome',
      ),
      currentYear: {
        value: data.estimatedNationalEquivalentOfInvestmentIncome,
        isAggregateValueZero: false,
      },
      nextYear: {
        value: data.estimatedNationalEquivalentOfInvestmentIncomeNextYear,
        isAggregateValueZero: false,
      },
    },
    {
      key: 'business-income',
      mainHeaderName: 'hidden',
      headerName: t('business-income'),
      ...getSameFieldByYear(pastYearKeys, yearToFundItemMap, 'businessIncome'),
      currentYear: {
        value: data.estimatedBusinessIncome,
        isAggregateValueZero: false,
      },
      nextYear: {
        value: data.estimatedBusinessIncomeNextYear,
        isAggregateValueZero: false,
      },
    },
    {
      key: 'national-equivalent-amount-of-business-income',
      mainHeaderName: 'hidden',
      headerName: t('national-equivalent-amount-of-that'),
      ...getSameFieldByYear(
        pastYearKeys,
        yearToFundItemMap,
        'nationalEquivalentOfBusinessIncome',
      ),
      currentYear: {
        value: data.estimatedNationalEquivalentOfBusinessIncome,
        isAggregateValueZero: false,
      },
      nextYear: {
        value: data.estimatedNationalEquivalentOfBusinessIncomeNextYear,
        isAggregateValueZero: false,
      },
    },
    {
      key: 'others-income',
      mainHeaderName: 'hidden',
      headerName: t('others'),
      ...getSameFieldByYear(
        pastYearKeys,
        yearToFundItemMap,
        'otherIncomeAmount',
      ),
      currentYear: {
        value: data.estimatedOtherIncomeAmount,
        isAggregateValueZero: false,
      },
      nextYear: {
        value: data.estimatedOtherIncomeAmountNextYear,
        isAggregateValueZero: false,
      },
    },
    {
      key: 'total-amount',
      mainHeaderName: 'hidden',
      headerName: t('total-amount'),
      ...getYearToValueRecordOfFunc(
        pastYearKeys,
        yearToFundItemMap,
        'previous-year-performance',
        getIncomeSummaryByType,
        isDebugMode,
      ),
      currentYear: getIncomeSummaryByType(
        data,
        'estimated-this-year',
        isDebugMode,
      ),
      nextYear: getIncomeSummaryByType(
        data,
        'estimated-next-year',
        isDebugMode,
      ),
    },
    {
      key: 'expense',
      mainHeaderName: t('expense'),
      headerName: t('business-expenses'),
      ...getSameFieldByYear(pastYearKeys, yearToFundItemMap, 'businessExpense'),
      currentYear: {
        value: data.estimatedBusinessExpense,
        isAggregateValueZero: false,
      },
      nextYear: {
        value: data.estimatedBusinessExpenseNextYear,
        isAggregateValueZero: false,
      },
    },
    {
      key: 'administrative-expenses',
      mainHeaderName: 'hidden',
      headerName: t('administrative-expenses'),
      ...getSameFieldByYear(
        pastYearKeys,
        yearToFundItemMap,
        'administrativeExpense',
      ),
      currentYear: {
        value: data.estimatedAdministrativeExpense,
        isAggregateValueZero: false,
      },
      nextYear: {
        value: data.estimatedAdministrativeExpenseNextYear,
        isAggregateValueZero: false,
      },
    },
    {
      key: 'administrative-expenses-rate',
      mainHeaderName: 'hidden',
      headerName: t('administrative-expenses-rate'),
      ...getAdministrativeExpensesRateByYear(
        pastYearKeys,
        yearToFundItemMap,
        isDebugMode,
      ),
      currentYear: {
        value: calcAdministrativeExpensesRate(
          data.estimatedAdministrativeExpense ?? 0,
          getExpenditureSummaryByType(data, 'estimated-this-year', isDebugMode)
            .value,
        ),
        isAggregateValueZero: false,
      },
      nextYear: {
        value: calcAdministrativeExpensesRate(
          data.estimatedAdministrativeExpenseNextYear ?? 0,
          getExpenditureSummaryByType(data, 'estimated-next-year', isDebugMode)
            .value,
        ),
        isAggregateValueZero: false,
      },
    },
    {
      key: 'chore-expenses-of-fund-establishing-of-that',
      mainHeaderName: 'hidden',
      headerName: t('chore-expenses-of-fund-establishing-of-that'),
      ...getSameFieldByYear(
        pastYearKeys,
        yearToFundItemMap,
        'choreExpensesOfFundEstablishing',
      ),
      currentYear: {
        value: data.estimatedChoreExpensesOfFundEstablishing,
        isAggregateValueZero: false,
      },
      nextYear: {
        value: data.estimatedChoreExpensesOfFundEstablishingNextYear,
        isAggregateValueZero: false,
      },
    },
    {
      key: 'personnel-expenses-of-fund-establishing-of-that',
      mainHeaderName: 'hidden',
      headerName: t('personnel-expenses-of-fund-establishing-of-that'),
      ...getSameFieldByYear(
        pastYearKeys,
        yearToFundItemMap,
        'personnelExpensesOfFundEstablishing',
      ),
      currentYear: {
        value: data.estimatedPersonnelExpensesOfFundEstablishing,
        isAggregateValueZero: false,
      },
      nextYear: {
        value: data.estimatedPersonnelExpensesOfFundEstablishingNextYear,
        isAggregateValueZero: false,
      },
    },
    {
      key: 'total-epense-amount',
      mainHeaderName: 'hidden',
      headerName: t('total-amount'),
      ...getYearToValueRecordOfFunc(
        pastYearKeys,
        yearToFundItemMap,
        'previous-year-performance',
        getExpenditureSummaryByType,
        isDebugMode,
      ),
      currentYear: getExpenditureSummaryByType(
        data,
        'estimated-this-year',
        isDebugMode,
      ),
      nextYear: getExpenditureSummaryByType(
        data,
        'estimated-next-year',
        isDebugMode,
      ),
    },
    {
      key: 'returned-to-the-national-treasury',
      mainHeaderName: t('returned-to-the-national-treasury'),
      headerName: null,
      ...getSameFieldByYear(
        pastYearKeys,
        yearToFundItemMap,
        'amountReturnedToTheNationalTreasury',
      ),
      currentYear: {
        value: data.estimatedAmountReturnedToTheNationalTreasury,
        isAggregateValueZero: false,
      },
      nextYear: {
        value: data.estimatedAmountReturnedToTheNationalTreasuryNextYear,
        isAggregateValueZero: false,
      },
    },
    {
      key: 'returned-to-the-organization',
      mainHeaderName: isBefore2025({ fiscalYear })
        ? t('returned-to-the-organization-before-2025')
        : t('returned-to-the-organization'),
      headerName: null,
      ...getSameFieldByYear(
        pastYearKeys,
        yearToFundItemMap,
        'amountReturnedToTheOrganization',
      ),
      currentYear: {
        value: data.estimatedAmountReturnedToTheOrganization,
        isAggregateValueZero: false,
      },
      nextYear: {
        value: data.estimatedAmountReturnedToTheOrganizationNextYear,
        isAggregateValueZero: false,
      },
    },
    {
      key: 'fund-balance-at-the-end-of-fiscal-year',
      mainHeaderName: t('fund-balance-at-the-end-of-fiscal-year'),
      headerName: null,
      ...getIncomeAndExpenditureSummariesByYear(
        pastYearKeys,
        yearToFundItemMap,
        'previous-year-performance',
        getIncomeAndExpenditureSummaryByType,
        getIncomeSummaryByType,
        getExpenditureSummaryByType,
        isDebugMode,
      ),
      currentYear: getIncomeAndExpenditureSummaryByType(
        data,
        'estimated-this-year',
        getIncomeSummaryByType,
        getExpenditureSummaryByType,
        isDebugMode,
      ),
      nextYear: getIncomeAndExpenditureSummaryByType(
        data,
        'estimated-next-year',
        getIncomeSummaryByType,
        getExpenditureSummaryByType,
        isDebugMode,
      ),
    },
    {
      key: 'national-equivalent-amount-of-fund-balance',
      mainHeaderName: t('national-equivalent-amount-of-that'),
      headerName: null,
      ...getSameFieldByYear(
        pastYearKeys,
        yearToFundItemMap,
        'nationalEquivalentOfTheBalance',
      ),
      currentYear: {
        value: data.estimatedNationalEquivalentOfTheBalance,
        isAggregateValueZero: false,
      },
      nextYear: {
        value: data.estimatedNationalEquivalentOfTheBalanceNextYear,
        isAggregateValueZero: false,
      },
    },
    {
      key: 'clerical-personnel-expenses',
      mainHeaderName: t('clerical-personnel-expenses'),
      headerName: t('chore-expense'),
      ...getSameFieldByYear(
        pastYearKeys,
        yearToFundItemMap,
        'choreExpensesNotFromThisFund',
      ),
      currentYear: {
        value: data.estimatedChoreExpensesNotFromThisFund,
        isAggregateValueZero: false,
      },
      nextYear: {
        value: data.estimatedChoreExpensesNotFromThisFundNextYear,
        isAggregateValueZero: false,
      },
    },
    {
      key: 'personnel-expense',
      mainHeaderName: 'hidden',
      headerName: t('personnel-expense'),
      ...getSameFieldByYear(
        pastYearKeys,
        yearToFundItemMap,
        'personnelExpensesNotFromThisFund',
      ),
      currentYear: {
        value: data.estimatedPersonnelExpensesNotFromThisFund,
        isAggregateValueZero: false,
      },
      nextYear: {
        value: data.estimatedPersonnelExpensesNotFromThisFundNextYear,
        isAggregateValueZero: false,
      },
    },
    {
      key: 'total-amount-clerical-expenses',
      mainHeaderName: 'hidden',
      headerName: t('total-amount'),
      ...getYearToValueRecordOfFunc(
        pastYearKeys,
        yearToFundItemMap,
        'previous-year-performance',
        getClericalExpenditureSummaryByType,
        isDebugMode,
      ),
      currentYear: getClericalExpenditureSummaryByType(
        data,
        'estimated-this-year',
        isDebugMode,
      ),
      nextYear: getClericalExpenditureSummaryByType(
        data,
        'estimated-next-year',
        isDebugMode,
      ),
    },
  ];
};
